import { useEffect, useMemo } from 'react';
import { useNameMapQuery, useOrgMapQuery } from '@features/redux/apis/org';
import { FormMultiSelect } from '@components/forms/dropdowns/base/FormMultiSelect';
// todo: Kill unneeded utils
import { createDropdownHandler, createOptionMap, initOptions } from '@utils/data';

import type { UseStateTuple } from '@/types/general';
import type { Option } from './base/FormMultiSelect';

export type SelectedState = Record<'companies' | 'resellers' | 'locations', Option[]>;

type MultiOrgDropdownsProps = {
    state: UseStateTuple<SelectedState>;
    /**
     * A map of claim selection strings to use for initial selection state.
     */
    populate?: {
        [K in keyof SelectedState]?: string;
    };
};

const hasAll = (options: Option[]) => options.some(({ id }) => id === '*');

const companyHandler = createDropdownHandler('companies', ['locations', 'resellers']);
const resellerHandler = createDropdownHandler('resellers', ['locations']);
const locationHandler = createDropdownHandler('locations', []);

export const MultiOrgDropdowns = ({ state: [selected, setSelected], populate }: MultiOrgDropdownsProps) => {
    const { data: nameMap, isLoading: nameMapLoading } = useNameMapQuery({});
    const { companies = {}, resellers = {}, locations = {} } = nameMap || {};

    const {
        data: { companyLocationMap = {}, companyResellerMap = {}, resellerLocationMap = {} } = {},
        isLoading: orgMapLoading,
    } = useOrgMapQuery({});

    const options = {
        companies: useMemo(() => Object.entries(companies).map(([id, name]) => ({ id, name })), [companies]),
        resellers: useMemo(() => createOptionMap(companyResellerMap, resellers), [resellers, companyResellerMap]),
        locations: useMemo(
            () => createOptionMap({ ...companyLocationMap, ...resellerLocationMap }, locations),
            [locations, companyLocationMap, resellerLocationMap]
        ),
    };

    const locationOptions = useMemo(() => {
        if (orgMapLoading || nameMapLoading) return [];

        if (hasAll(selected.resellers) && hasAll(selected.companies)) {
            return Object.values(options.locations).flat();
        }

        if (hasAll(selected.resellers) && !hasAll(selected.companies)) {
            const resellers = selected.companies
                .map(({ id }) => options.resellers[id])
                .flat()
                .filter(Boolean);

            const resellerLocations = resellers.map(({ id }) => options.locations[id]).flat();
            const companyLocations = selected.companies.map(({ id }) => options.locations[id]).flat();
            return [...resellerLocations, ...companyLocations].filter(Boolean);
        }

        return selected.resellers
            .map(({ id }) => options.locations[id])
            .flat()
            .filter(Boolean);
    }, [selected.resellers, selected.companies, options.locations, orgMapLoading, nameMapLoading]);

    // Init selected options from claim selections
    useEffect(() => {
        if (!populate || nameMapLoading) return;

        setSelected((prev) => ({
            ...prev,
            ...initOptions(populate!, nameMap!),
        }));
    }, [nameMapLoading]);

    return (
        <>
            <FormMultiSelect
                loading={nameMapLoading}
                size={12}
                label='Companies'
                options={options.companies}
                selected={selected.companies}
                onChange={companyHandler(setSelected)}
            />

            <FormMultiSelect
                loading={nameMapLoading || orgMapLoading}
                size={12}
                disable={!selected.companies.length}
                label='Resellers'
                options={
                    hasAll(selected.companies)
                        ? Object.values(options.resellers).flat()
                        : selected.companies.flatMap(({ id }) => options.resellers[id] || [])
                }
                selected={selected.resellers}
                onChange={resellerHandler(setSelected)}
            />

            <FormMultiSelect
                loading={nameMapLoading || orgMapLoading}
                size={12}
                disable={!selected.companies.length}
                label='Locations'
                options={locationOptions}
                selected={selected.locations}
                onChange={locationHandler(setSelected)}
            />
        </>
    );
};
